import { chakra, Flex, Table, Tbody, Td, Th, Thead, Tr, Text, HStack, IconButton, Button, Skeleton } from '@chakra-ui/react';
import { useCallback, useEffect, useRef } from 'react';
import { useReactTable, flexRender, getCoreRowModel, SortingState, Updater, ColumnDef, RowData } from '@tanstack/react-table';
import { ChevronLeftIcon, ChevronRightIcon, TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import { PageSizeSelector } from '@/components/ListPaginationControl/PageSizeSelector';
import { ELLIPSIS, getPagesOptions } from '@/components/ListPaginationControl/utils/getPagesOptions';
import { SkeletonTable } from '@/components/SkeletonTable/SkeletonTable';

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    updateData: (rowIndex: number, columnId: string, value: unknown) => void;
  }
}

const useSkipper = () => {
  const shouldSkipRef = useRef(true);
  const shouldSkip = shouldSkipRef.current;

  // Wrap a function with this to skip a pagination reset temporarily
  const skip = useCallback(() => {
    shouldSkipRef.current = false;
  }, []);

  useEffect(() => {
    shouldSkipRef.current = true;
  });

  return [shouldSkip, skip] as const;
};

export interface TableProps<T> {
  data: T[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  columns: ColumnDef<any, any>[];
  onSortingChange?: (field: string, desc: boolean) => void;
  onHandleChangeData?: (rowIndex: number, columnID: string, value: any) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
}

export const CustomTable = <T,>({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  sorting,
  totalItems,
  columns,
  onSortingChange,
  onPaginationChange,
  onHandleChangeData,
}: TableProps<T>) => {
  const onSorting = useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      if (!sorting || !onSortingChange) {
        return;
      }

      const state = [{ id: sorting.field, desc: sorting.desc }] as SortingState;
      const newSortingValue = updaterOrValue instanceof Function ? updaterOrValue(state) : updaterOrValue;

      const sort = (newSortingValue as SortingState).shift();
      onSortingChange(sort?.id || '', !!sort?.desc);
    },
    [onSortingChange, sorting],
  );

  const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper();

  const updateData = useCallback(
    (rowIndex: number, columnID: string, value: any) => {
      // Skip page index reset until after next rerender
      skipAutoResetPageIndex();
      onHandleChangeData?.(rowIndex, columnID, value);
    },
    [onHandleChangeData, skipAutoResetPageIndex],
  );

  const table = useReactTable({
    columns,
    data: data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: onSorting,
    manualSorting: true,
    manualPagination: true,
    enableMultiSort: false,
    enableSorting: onSortingChange !== undefined,
    autoResetPageIndex, //turn off auto reset of pageIndex
    state: {
      sorting: sorting
        ? [
            {
              id: sorting.field,
              desc: sorting.desc,
            },
          ]
        : [],
      pagination: {
        pageIndex: pageNumber ? pageNumber - 1 : 0,
        pageSize: pageSize || 10,
      },
    },
    pageCount: totalPages || -1,
    meta: {
      updateData: updateData,
    },
  });

  const pageOptions = getPagesOptions({ currentPage: table.getState().pagination.pageIndex, totalPages: table.getPageCount() });

  return (
    <Flex direction='column' gap='micro'>
      <Flex overflow='auto'>
        <Table variant={'striped'} mb='4'>
          <Thead
            sx={{
              '& th': {
                py: '8px',
              },
            }}
          >
            {table.getHeaderGroups().map(headerGroup => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const meta: any = header.column.columnDef.meta;
                  return (
                    <Th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      isNumeric={meta?.isNumeric}
                      textAlign={meta?.align || undefined}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}

                      <chakra.span pl='4'>
                        {header.column.getIsSorted() ? (
                          header.column.getIsSorted() === 'desc' ? (
                            <TriangleDownIcon boxSize={3} aria-label='sorted descending' />
                          ) : (
                            <TriangleUpIcon boxSize={3} aria-label='sorted ascending' />
                          )
                        ) : null}
                      </chakra.span>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {isLoading && <SkeletonTable cols={columns.length} />}
            {!isLoading &&
              table.getRowModel().rows.map(row => (
                <Tr key={row.id}>
                  {row.getVisibleCells().map(cell => {
                    const meta: any = cell.column.columnDef.meta;
                    return (
                      <Td key={cell.id} isNumeric={meta?.isNumeric}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    );
                  })}
                </Tr>
              ))}
          </Tbody>
        </Table>
      </Flex>
      {isLoading && <Skeleton h='kilo' />}
      {!isLoading && (
        <Flex flexWrap='wrap' gap='deka'>
          <Flex direction='row' alignItems='center' gap={4}>
            <PageSizeSelector
              value={table.getState().pagination.pageSize}
              onChange={e => onPaginationChange?.(pageNumber, Number(e.target.value))}
            />
            <div className='separator'></div>
            <Text>{`${table.getState().pagination.pageIndex + 1}–${table.getPageCount()} de ${totalItems || 0} itens`}</Text>
          </Flex>
          <HStack alignItems='center' justifyContent='end' spacing='deka' flexGrow={1}>
            <IconButton
              aria-label={'Página anterior'}
              icon={<ChevronLeftIcon boxSize={5} color='gray.800' />}
              isDisabled={!table.getCanPreviousPage()}
              onClick={() => onPaginationChange?.(pageNumber - 1, pageSize)}
              variant='ghost'
            />
            <HStack>
              {pageOptions.map((pageOption, index) => {
                const isEllipsis = pageOption === ELLIPSIS;
                const isCurrentPage = pageOption === table.getState().pagination.pageIndex + 1;

                return (
                  <Button
                    key={`${pageOption}-${index}`}
                    variant={isCurrentPage ? 'solid' : 'ghost'}
                    size='xs'
                    colorScheme={isCurrentPage ? 'brand' : 'neutral'}
                    px='nano'
                    borderRadius='lg'
                    isDisabled={isEllipsis}
                    onClick={!isEllipsis ? () => onPaginationChange?.(Number(pageOption), pageSize) : undefined}
                  >
                    {pageOption}
                  </Button>
                );
              })}
            </HStack>
            <IconButton
              aria-label={'Próxima página'}
              icon={<ChevronRightIcon boxSize={5} color='gray.800' />}
              isDisabled={!table.getCanNextPage()}
              onClick={() => onPaginationChange?.(pageNumber + 1, pageSize)}
              variant='ghost'
            />
          </HStack>
        </Flex>
      )}
    </Flex>
  );
};
